import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useNavigationType, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';

interface User {
  id: string;
  teamName: string;
}

interface Season {
  id: string;
  name: string;
  gameType: string;
  countryCode: string;
  isActive: boolean;
}

interface League {
  id: string;
  name: string;
  description: string;
  creator: User;
  season: Season;
  type: 'public' | 'invite_only' | 'fanleague' | 'official';
  isActive: boolean;
  maxMembers: number;
  currentMembers: number;
  inviteCode?: string;
  groupname?: string;
  createdAt: string;
}

const Leagues: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [userLeagues, setUserLeagues] = useState<League[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'my' | 'public' | 'invite_only' | 'fanleague' | 'official' | 'private'>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  // Create league form state (non-admins can only create invite_only; default to invite_only for them)
  const isAdmin = user?.role === 'admin';
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    season: '',
    type: 'invite_only' as 'public' | 'invite_only' | 'fanleague' | 'official',
    groupname: ''
  });

  // Scroll position management
  const scrollRestored = useRef(false);

  useEffect(() => {
    // If this is a back navigation, restore scroll position
    if (navigationType === 'POP' && !scrollRestored.current) {
      const savedScrollPosition = sessionStorage.getItem('scroll_leagues');
      if (savedScrollPosition) {
        const scrollY = parseInt(savedScrollPosition);
        
        // Mark that we've restored scroll for this navigation
        scrollRestored.current = true;
        
        // Try to restore scroll position multiple times to ensure it works
        const restoreScroll = () => {
          if (window.scrollY !== scrollY) {
            window.scrollTo(0, scrollY);
          }
        };
        
        // First attempt after 100ms
        const timer1 = setTimeout(restoreScroll, 100);
        
        // Second attempt after 300ms in case first one didn't work
        const timer2 = setTimeout(restoreScroll, 300);
        
        // Third attempt after 500ms as a final fallback
        const timer3 = setTimeout(restoreScroll, 500);
        
        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
        };
      }
    }
    
    // Reset the flag for new navigations
    if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      scrollRestored.current = false;
    }
  }, [navigationType, leagues, userLeagues]); // Added data dependencies

  // Save scroll position when leaving
  useEffect(() => {
    return () => {
      const scrollPosition = window.scrollY;
      
      // Only save if we're actually leaving the page (not just re-rendering)
      if (window.location.pathname !== '/leagues') {
        sessionStorage.setItem('scroll_leagues', scrollPosition.toString());
      }
    };
  }, []);

  useEffect(() => {
    const fetchLeaguesData = async () => {
      // Check if we have cached data with expiration
      const cachedLeagues = sessionStorage.getItem('leagues_data');
      const cachedUserLeagues = sessionStorage.getItem('user_leagues_data');
      const cachedSeasons = sessionStorage.getItem('seasons_data');
      const cacheTimestamp = sessionStorage.getItem('leagues_cache_timestamp');
      
      if (cachedLeagues && cachedUserLeagues && cachedSeasons && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedLeagues = JSON.parse(cachedLeagues);
            const parsedUserLeagues = JSON.parse(cachedUserLeagues);
            const parsedSeasons = JSON.parse(cachedSeasons);
            
            setLeagues(parsedLeagues);
            setUserLeagues(parsedUserLeagues);
            setSeasons(parsedSeasons);
            
            // Set default filter to 'my' if user has leagues, otherwise 'official'
            if (parsedUserLeagues.length > 0) {
              setFilter('my');
            } else {
              setFilter('official');
            }
            
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('leagues_data');
            sessionStorage.removeItem('user_leagues_data');
            sessionStorage.removeItem('seasons_data');
            sessionStorage.removeItem('leagues_cache_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('leagues_data');
          sessionStorage.removeItem('user_leagues_data');
          sessionStorage.removeItem('seasons_data');
          sessionStorage.removeItem('leagues_cache_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      fetchLeagues();
    };

    fetchLeaguesData();
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    if (location.state?.openCreateForm) {
      setShowCreateForm(true);
      if (!isAdmin) {
        setCreateForm(prev => ({ ...prev, type: 'invite_only', groupname: '' }));
      }

      const scrollToTop = () => window.scrollTo(0, 0);
      scrollToTop();
      requestAnimationFrame(scrollToTop);
      const timer = setTimeout(scrollToTop, 100);

      navigate(location.pathname, { replace: true, state: null });

      return () => clearTimeout(timer);
    }
  }, [location.state, isAdmin, navigate, location.pathname]);

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      
      // Fetch all leagues
      const allLeaguesResponse = await apiClient.get('/leagues');
      setLeagues(allLeaguesResponse.data);
      
      // Fetch user's leagues
      const userLeaguesResponse = await apiClient.get('/leagues/my');
      // Extract league data from memberships
      const userLeaguesData = (userLeaguesResponse.data || []).map((membership: any) => ({
        ...membership.league,
        id: membership.league?.id || membership.id,
        membershipId: membership.id,
        role: membership.role,
        totalPoints: membership.totalPoints
      }));
      setUserLeagues(userLeaguesData);
      
      if (!isAdmin || userLeaguesData.length > 0) {
        setFilter('my');
      } else {
        setFilter('official');
      }
      
      // Fetch seasons for the create form
      const seasonsResponse = await apiClient.get('/seasons');
      setSeasons(seasonsResponse.data);
      
      // Cache all the data
      sessionStorage.setItem('leagues_data', JSON.stringify(allLeaguesResponse.data));
      sessionStorage.setItem('user_leagues_data', JSON.stringify(userLeaguesData));
      sessionStorage.setItem('seasons_data', JSON.stringify(seasonsResponse.data));
      sessionStorage.setItem('leagues_cache_timestamp', Date.now().toString());
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load leagues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createForm.name.trim()) {
      setError('League name is required');
      return;
    }

    if (!createForm.season) {
      setError('Please select a season');
      return;
    }

    if (createForm.type === 'fanleague' && !createForm.groupname.trim()) {
      setError('Group name is required for fan leagues');
      return;
    }

    if (createForm.type === 'fanleague' && createForm.groupname.length < 3) {
      setError('Group name must be at least 3 characters long');
      return;
    }

    try {
      setError('');
      // Non-admins may only create private invite-only leagues
      const effectiveType = isAdmin ? createForm.type : 'invite_only';
      const formForSubmit = { ...createForm, type: effectiveType };
      const { groupname, ...baseData } = formForSubmit;
      const leagueData = formForSubmit.type === 'fanleague'
        ? formForSubmit
        : baseData;

      await apiClient.post('/leagues', leagueData);
      setShowCreateForm(false);
      setCreateForm({ name: '', description: '', season: '', type: isAdmin ? 'public' : 'invite_only', groupname: '' });
      
      // Clear the league cache so the new league appears immediately
      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      // Also clear home page league caches
      sessionStorage.removeItem('home_user_leagues_data');
      sessionStorage.removeItem('home_user_leagues_timestamp');
      sessionStorage.removeItem('home_main_user_leagues_data');
      sessionStorage.removeItem('home_main_user_leagues_timestamp');
      
      fetchLeagues(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create league');
    }
  };

  const handleJoinByCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!joinCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    try {
      setJoining(true);
      setError('');
      await apiClient.post('/leagues/join-by-code', { inviteCode: joinCode });
      setJoinCode('');
      
      // Clear the league cache so the joined league appears immediately
      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      // Also clear home page league caches
      sessionStorage.removeItem('home_user_leagues_data');
      sessionStorage.removeItem('home_user_leagues_timestamp');
      sessionStorage.removeItem('home_main_user_leagues_data');
      sessionStorage.removeItem('home_main_user_leagues_timestamp');
      
      fetchLeagues(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to join league');
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveLeague = async (leagueId: string) => {
    if (!window.confirm('Are you sure you want to leave this league?')) {
      return;
    }

    try {
      await apiClient.delete(`/leagues/${leagueId}/leave`);
      
      // Clear the league cache so the league removal is reflected immediately
      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      // Also clear home page league caches
      sessionStorage.removeItem('home_user_leagues_data');
      sessionStorage.removeItem('home_user_leagues_timestamp');
      sessionStorage.removeItem('home_main_user_leagues_data');
      sessionStorage.removeItem('home_main_user_leagues_timestamp');
      
      fetchLeagues(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to leave league');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFilteredLeagues = () => {
    if (!isAdmin) {
      return userLeagues;
    }

    switch (filter) {
      case 'my':
        return userLeagues;
      case 'fanleague':
        return leagues.filter(league => league.type === 'fanleague');
      case 'official':
        return leagues.filter(league => league.type === 'official');
      case 'private':
        // For admins: show invite_only leagues they're not members of
        if (user?.role === 'admin') {
          return leagues.filter(league => 
            league.type === 'invite_only' && 
            !userLeagues.some(userLeague => userLeague.id === league.id)
          );
        }
        return [];
      default:
        // For users with leagues: show only public leagues when "All Leagues" is selected
        // For users without leagues: show only public leagues (no tabs shown)
        if (userLeagues.length > 0) {
          return leagues.filter(league => league.type === 'public');
        } else {
          // For users with no leagues, show all public leagues
          return leagues.filter(league => league.type === 'public');
        }
    }
  };

  const isUserInLeague = (leagueId: string): boolean => {
    return userLeagues.some(league => league.id === leagueId);
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading leagues...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
        </Link>
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Friends Leagues</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Create your own private friends league or use an invite code to join an existing friends league
          </p>
        </div>

        {error && (
          <div className="border px-4 py-3 rounded mb-6" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 card rounded-lg p-4 md:p-5 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: 'var(--text-secondary)' }}>
                Start your own
              </p>
              <button
                onClick={() => {
                  const next = !showCreateForm;
                  if (next && !isAdmin) {
                    setCreateForm(prev => ({ ...prev, type: 'invite_only', groupname: '' }));
                  }
                  setShowCreateForm(next);
                }}
                className="btn-primary inline-flex items-center justify-center w-full md:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md"
              >
                {showCreateForm ? 'Cancel' : 'Create Private Friends League'}
              </button>
            </div>

            <div className="space-y-2 md:border-l md:border-gray-200 md:dark:border-gray-700 md:pl-6">
              <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: 'var(--text-secondary)' }}>
                Join with code
              </p>
              <form onSubmit={handleJoinByCode} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Enter invite code to join friend league"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  className="input-field px-3 py-2 rounded-md text-sm flex-1"
                />
                <button
                  type="submit"
                  disabled={joining}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50"
                  style={{ backgroundColor: '#22c55e' }}
                >
                  {joining ? 'Joining...' : 'Join'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Create League Form */}
        {showCreateForm && (
          <div className="card rounded-lg shadow p-6 mb-6 bg-gray-50 dark:bg-gray-800/50">
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Create Private Friends League</h2>
            <form onSubmit={handleCreateLeague} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  League Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="input-field mt-1 block w-full rounded-md px-3 py-2"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Description
                </label>
                <textarea
                  id="description"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={3}
                  className="input-field mt-1 block w-full rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="season" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Competition *
                </label>
                <select
                  id="season"
                  value={createForm.season}
                  onChange={(e) => setCreateForm({ ...createForm, season: e.target.value })}
                  className="input-field mt-1 block w-full rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select a competition</option>
                  {seasons.filter(season => season.isActive).map(season => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>

              {isAdmin && (
                <div>
                  <label htmlFor="type" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    League Type
                  </label>
                  <select
                    id="type"
                    value={createForm.type}
                    onChange={(e) => {
                      const newType = e.target.value as 'public' | 'invite_only' | 'fanleague' | 'official';
                      setCreateForm({
                        ...createForm,
                        type: newType,
                        groupname: newType === 'fanleague' ? createForm.groupname : ''
                      });
                    }}
                    className="input-field mt-1 block w-full rounded-md px-3 py-2"
                  >
                    <option value="public">Public - Anyone can join</option>
                    <option value="invite_only">Invite Only - Requires invite code</option>
                    <option value="official">Official - Official competition league</option>
                    <option value="fanleague">Fan League - Custom branded league</option>
                  </select>
                </div>
              )}

              {/* Group Name field - only for fanleagues */}
              {createForm.type === 'fanleague' && (
                <div>
                  <label htmlFor="groupname" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Group Name (URL) *
                  </label>
                  <input
                    type="text"
                    id="groupname"
                    value={createForm.groupname}
                    onChange={(e) => setCreateForm({ ...createForm, groupname: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    className="input-field mt-1 block w-full rounded-md px-3 py-2"
                    placeholder="e.g. arsenal-fc, man-united"
                    pattern="^[a-z0-9-]+$"
                    minLength={3}
                    maxLength={50}
                    required={createForm.type === 'fanleague'}
                  />
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    This creates a custom URL: yoursite.com/{createForm.groupname || 'groupname'}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Only lowercase letters, numbers, and hyphens allowed
                  </p>
                </div>
              )}



              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary px-4 py-2 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-4 py-2 rounded-md text-sm font-medium"
                >
                  Create League
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filter tabs — admins only */}
        {isAdmin && (
          userLeagues.length > 0 ? (
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('my')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'my' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  My Friends Leagues ({userLeagues.length})
                </button>
                <button
                  onClick={() => setFilter('official')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'official' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Official Leagues
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'all' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  User Leagues
                </button>
                <button
                  onClick={() => setFilter('fanleague')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'fanleague' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Fan Leagues
                </button>
                <button
                  onClick={() => setFilter('private')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'private' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Private User Leagues ({leagues.filter(league =>
                    league.type === 'invite_only' &&
                    !userLeagues.some(userLeague => userLeague.id === league.id)
                  ).length})
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('official')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'official' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Official Leagues
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'all' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  User Leagues
                </button>
                <button
                  onClick={() => setFilter('fanleague')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'fanleague' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Fan Leagues
                </button>
                <button
                  onClick={() => setFilter('private')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    filter === 'private' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Private User Leagues ({leagues.filter(league =>
                    league.type === 'invite_only' &&
                    !userLeagues.some(userLeague => userLeague.id === league.id)
                  ).length})
                </button>
              </div>
            </div>
          )
        )}

        {/* Leagues List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {getFilteredLeagues().length === 0 ? (
            <div className="col-span-full py-8 w-full">
              {!isAdmin ? (
                <div className="card rounded-lg shadow w-full p-6 md:p-8 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-6">
                    <img
                      src="/files/images/predictorpro-guys.png"
                      alt="Predictor Pro Guys"
                      className="w-72 md:w-72 h-auto mx-auto md:mx-0 flex-shrink-0"
                    />
                    <div className="text-center md:text-left md:flex-1 md:pr-6">
                      <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        No friends leagues yet!
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        You haven&apos;t joined any leagues yet - create your own private friends league or use an invite code above to get started.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="py-4" style={{ color: 'var(--text-secondary)' }}>
                  No leagues found for this filter.
                </p>
              )}
            </div>
          ) : (
            getFilteredLeagues().map(league => (
              <div key={league.id} className="card shadow rounded-lg hover:shadow-lg transition-shadow duration-200 overflow-hidden bg-gray-50 dark:bg-gray-800/50">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div>
                        <Link to={`/leagues/${league.id}`}>
                        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{league.name || 'Unnamed League'}</h3>
                        </Link>
                        <p className="text-sm capitalize" style={{ color: 'var(--text-secondary)' }}>{league.season?.name || 'Unknown Competition'}</p>
                        {league.type !== 'official' && (
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {league.currentMembers ?? 0} {(league.currentMembers ?? 0) === 1 ? 'member' : 'members'}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full`} style={{
                      backgroundColor: league.type === 'public' 
                        ? 'rgba(34, 197, 94, 0.1)'
                        : league.type === 'invite_only'
                        ? 'rgba(234, 179, 8, 0.1)'
                        : league.type === 'official'
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(219, 255, 0, 0.2)' ,
                      color: league.type === 'public' 
                        ? '#22c55e' 
                        : league.type === 'invite_only'
                        ? '#f4ff00'
                        : league.type === 'official'
                        ? '#22c55e' 
                        : '#dbff00'
                    }}>
                      {(league.type || 'unknown').replace('_', ' ')}
                    </span>
                  </div>
                  
                  <p className="text-sm mb-8 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                    {league.description || 'No description available'}
                  </p>

                  <div className="flex justify-start">
                    {isUserInLeague(league.id) ? (
                      <Link
                        to={`/leagues/${league.id}`}
                        className="btn-primary text-black text-sm font-medium"
                      >
                        View League →
                      </Link>
                    ) : (
                      <>
                        {league.type === 'public' && (
                          <button
                            onClick={async () => {
                              try {
                                await apiClient.post(`/leagues/${league.id}/join`);
                                
                                // Clear the league cache so the joined league appears immediately
                                sessionStorage.removeItem('leagues_data');
                                sessionStorage.removeItem('user_leagues_data');
                                sessionStorage.removeItem('leagues_cache_timestamp');
                                // Also clear home page league caches
                                sessionStorage.removeItem('home_user_leagues_data');
                                sessionStorage.removeItem('home_user_leagues_timestamp');
                                sessionStorage.removeItem('home_main_user_leagues_data');
                                sessionStorage.removeItem('home_main_user_leagues_timestamp');
                                
                                navigate(`/leagues/${league.id}`);
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Failed to join league');
                              }
                            }}
                            className="btn-primary bg-green-600 text-white text-sm font-medium"
                          >
                            Join League →
                          </button>
                        )}
                        {league.type === 'official' && (
                          <button
                            onClick={async () => {
                              try {
                                await apiClient.post(`/leagues/${league.id}/join`);
                                
                                // Clear the league cache so the joined league appears immediately
                                sessionStorage.removeItem('leagues_data');
                                sessionStorage.removeItem('user_leagues_data');
                                sessionStorage.removeItem('leagues_cache_timestamp');
                                // Also clear home page league caches
                                sessionStorage.removeItem('home_user_leagues_data');
                                sessionStorage.removeItem('home_user_leagues_timestamp');
                                sessionStorage.removeItem('home_main_user_leagues_data');
                                sessionStorage.removeItem('home_main_user_leagues_timestamp');
                                
                                navigate(`/leagues/${league.id}`);
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Failed to join league');
                              }
                            }}
                            className="btn-primary bg-green-600 text-white text-sm font-medium"
                          >
                            Join League →
                          </button>
                        )}
                        {league.type === 'invite_only' && (
                          <>
                            {filter === 'private' && user?.role === 'admin' ? (
                              <Link
                                to={`/leagues/${league.id}`}
                                className="btn-secondary text-sm font-medium"
                              >
                                View League →
                              </Link>
                            ) : (
                              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Requires invite code</span>
                            )}
                          </>
                        )}
                        {league.type === 'fanleague' && (
                          <button
                            onClick={async () => {
                              try {
                                await apiClient.post(`/leagues/${league.id}/join`);
                                
                                // Clear the league cache so the joined league appears immediately
                                sessionStorage.removeItem('leagues_data');
                                sessionStorage.removeItem('user_leagues_data');
                                sessionStorage.removeItem('leagues_cache_timestamp');
                                // Also clear home page league caches
                                sessionStorage.removeItem('home_user_leagues_data');
                                sessionStorage.removeItem('home_user_leagues_timestamp');
                                sessionStorage.removeItem('home_main_user_leagues_data');
                                sessionStorage.removeItem('home_main_user_leagues_timestamp');
                                
                                navigate(`/leagues/${league.id}`);
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Failed to join league');
                              }
                            }}
                            className="btn-primary bg-green-600 text-white text-sm font-medium"
                          >
                            Join League →
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>


      </div>
    </div>
  );
};

export default Leagues; 